import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
// ────────────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────────────
export const lbKeys = {
    list: (gameType, scope, period, limit) => ['leaderboard', gameType, scope, period, limit],
    position: (gameType, scope, period) => ['leaderboard', 'me', gameType, scope, period],
    pbs: (playerId) => ['personal-bests', playerId],
};
// ────────────────────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────────────────────
/** Top N du leaderboard pour un mini-jeu donné. */
export function useLeaderboard(input) {
    const scope = input.scope ?? 'global';
    const period = input.period ?? 'all_time';
    const limit = input.limit ?? 50;
    return useQuery({
        queryKey: lbKeys.list(input.gameType, scope, period, limit),
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_leaderboard', {
                p_game_type: input.gameType,
                p_scope: scope,
                p_period: period,
                p_limit: limit,
                p_offset: 0,
            });
            if (error)
                throw error;
            return (data ?? []).map((r) => ({
                rank: Number(r.rank),
                player_id: r.player_id,
                pseudo: r.pseudo,
                score: r.score,
                played_at: r.played_at,
                is_self: r.is_self,
            }));
        },
        staleTime: 30_000,
    });
}
/** Position du joueur courant (même hors top N). */
export function useMyLeaderboardPosition(input) {
    const { user } = useAuth();
    const scope = input.scope ?? 'global';
    const period = input.period ?? 'all_time';
    return useQuery({
        queryKey: lbKeys.position(input.gameType, scope, period),
        enabled: !!user?.id,
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_player_leaderboard_position', {
                p_game_type: input.gameType,
                p_scope: scope,
                p_period: period,
            });
            if (error)
                throw error;
            const row = Array.isArray(data) ? data[0] : data;
            if (!row)
                return null;
            return {
                rank: Number(row.rank),
                score: Number(row.score),
                played_at: row.played_at,
                total_players: Number(row.total_players),
            };
        },
        staleTime: 30_000,
    });
}
/** Records personnels d'un joueur (tous mini-jeux × modes confondus). */
export function usePlayerPBs(playerId) {
    return useQuery({
        queryKey: playerId ? lbKeys.pbs(playerId) : ['personal-bests', 'none'],
        enabled: !!playerId,
        queryFn: async () => {
            if (!playerId)
                return [];
            const { data, error } = await supabase
                .from('personal_bests')
                .select('*')
                .eq('player_id', playerId)
                .order('best_score', { ascending: false });
            if (error)
                throw error;
            return (data ?? []);
        },
        staleTime: 30_000,
    });
}
